import { Environment } from "@react-three/drei";
import { useRef, useEffect } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";

/**
 * 背景预设 - 专业摄影棚效果
 */
export const PRESETS = {
  // 径向摄影棚渐变 - 从中心到边缘
  radialStudio: {
    type: "radial" as const,
    topColor: "#fafafa", // 接近白色的浅灰
    centerColor: "#d0d0d0", // 柔和的中灰色过渡
    edgeColor: "#505050", // 温和的深灰色
    description: "径向摄影棚渐变 - 柔和专业背景",
  },
  // 线性摄影棚渐变 - 从上到下
  linearStudio: {
    type: "linear" as const,
    topColor: "#1E1E1E", // VS Code 默认背景灰
    bottomColor: "#E0DCD7", // 家居感浅灰
    description: "线性摄影棚渐变 - 专业演播室背景",
  },
  // 强对比度五断点渐变棋盘格背景 - 透明度检查风格
  checkerboard: {
    type: "checkerboard" as const,
    lightColor: "#f8f8f8", // 浅色方格（接近白色）
    darkColor: "#000000", // 顶部纯黑色方格（最大对比度）
    quarterColor: "#000000", // 25%位置保持纯黑色
    midColor: "#808080", // 50%位置中灰色（过渡中心）
    threeQuarterColor: "#f8f8f8", // 75%位置白色
    gridSize: 32, // 方格大小（像素）
    gradientEnabled: true, // 启用渐变效果
    gradientStart: 0, // 渐变开始位置（0=顶部）
    gradientEnd: 1, // 渐变结束位置（1=底部）
    gradientPoints: [0, 0.25, 0.5, 0.75, 1] as const, // 五个断点位置：黑色→黑色→中灰→白色→白色
    description: "强对比度五断点渐变棋盘格背景 - 专业透明度检查",
  },
} as const;

/**
 * 渐变预设类型
 */
export type PresetKey = keyof typeof PRESETS;

/**
 * 环境系统配置 - 组件内聚
 */
export const ENVIRONMENT_CONFIG = {
  defaultGradient: "linearStudio" as PresetKey,
  hdrFile: "/assets/textures/aerodynamics_workshop_1k.hdr",
};

/**
 * 颜色插值工具函数 - 在两个十六进制颜色之间插值
 */
function interpolateColor(
  color1: string,
  color2: string,
  factor: number,
): string {
  // 解析十六进制颜色值
  const hex1 = color1.replace("#", "");
  const hex2 = color2.replace("#", "");

  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  // 线性插值计算
  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  // 转换回十六进制格式
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * 多色插值工具函数 - 支持任意数量的颜色断点进行分段插值
 */
function interpolateMultipleColors(
  colors: string[], // 颜色数组
  stops: readonly number[], // 断点位置数组（必须与colors长度相同）
  factor: number, // 0-1之间的插值因子
): string {
  // 确保factor在有效范围内
  const clampedFactor = Math.max(0, Math.min(1, factor));

  // 找到factor所在的区间
  let segmentIndex = 0;
  for (let i = 0; i < stops.length - 1; i++) {
    if (clampedFactor >= stops[i] && clampedFactor <= stops[i + 1]) {
      segmentIndex = i;
      break;
    }
  }

  // 如果factor等于最后一个断点，返回最后一个颜色
  if (clampedFactor >= stops[stops.length - 1]) {
    return colors[colors.length - 1];
  }

  // 计算在当前区间内的局部插值因子
  const segmentStart = stops[segmentIndex];
  const segmentEnd = stops[segmentIndex + 1];
  const segmentRange = segmentEnd - segmentStart;
  const localFactor = (clampedFactor - segmentStart) / segmentRange;

  // 在两个相邻颜色之间插值
  return interpolateColor(
    colors[segmentIndex],
    colors[segmentIndex + 1],
    localFactor,
  );
}

/**
 * 创建 Canvas 纹理的工具函数
 */
function createCanvasTexture(background: (typeof PRESETS)[PresetKey]) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height =
    background.type === "checkerboard" && background.gradientEnabled
      ? 512 * 8 // 渐变棋盘格使用8倍高度实现整体渐变
      : 512; // 其他背景保持原尺寸
  const ctx = canvas.getContext("2d")!;

  // 根据背景类型绘制
  if (background.type === "radial") {
    // 径向渐变
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY);

    const gradientObj = ctx.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      radius,
    );
    gradientObj.addColorStop(0, background.centerColor);
    gradientObj.addColorStop(1, background.edgeColor);

    ctx.fillStyle = gradientObj;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else if (background.type === "linear") {
    // 线性渐变
    const gradientObj = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradientObj.addColorStop(0, background.topColor);
    gradientObj.addColorStop(1, background.bottomColor);

    ctx.fillStyle = gradientObj;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else if (background.type === "checkerboard") {
    // 渐变棋盘格背景
    const gridSize = background.gridSize;
    const cols = Math.ceil(canvas.width / gridSize);
    const rows = Math.ceil(canvas.height / gridSize);
    const gradientEnabled = background.gradientEnabled;

    if (gradientEnabled) {
      // === 整体渐变算法：每个方格纯色，基于全局位置计算颜色 ===

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const isLight = (row + col) % 2 === 0;

          if (isLight) {
            // 浅色方格保持固定颜色
            ctx.fillStyle = background.lightColor;
          } else {
            // 深色方格根据绝对垂直位置插值计算纯色
            // 直接基于行的绝对位置创建从顶部到底部的平滑渐变
            const verticalFactor = row / (rows - 1); // 0=顶部深色, 1=底部浅色

            // 深色方格颜色基于五断点插值
            const interpolatedColor = interpolateMultipleColors(
              [
                background.darkColor, // 0% - 顶部纯黑色
                background.quarterColor, // 25% - 深灰色
                background.midColor, // 50% - 中灰色
                background.threeQuarterColor, // 75% - 浅灰色
                background.lightColor, // 100% - 接近白色
              ],
              background.gradientPoints, // [0, 0.25, 0.5, 0.75, 1]
              verticalFactor, // 基于绝对位置的平滑插值
            );

            ctx.fillStyle = interpolatedColor;
          }

          ctx.fillRect(col * gridSize, row * gridSize, gridSize, gridSize);
        }
      }
    } else {
      // === 传统算法：固定颜色棋盘格 ===
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const isLight = (row + col) % 2 === 0;
          const fillColor = isLight
            ? background.lightColor
            : background.darkColor;

          ctx.fillStyle = fillColor;
          ctx.fillRect(col * gridSize, row * gridSize, gridSize, gridSize);
        }
      }
    }
  }

  return new THREE.CanvasTexture(canvas);
}

/**
 * 渐变背景效果 - 使用 scene.background 平面背景
 */
function GradientBackgroundEffect({
  background,
}: {
  background: (typeof PRESETS)[PresetKey];
}) {
  const { scene } = useThree();

  useEffect(() => {
    const texture = createCanvasTexture(background);
    scene.background = texture;

    return () => {
      texture.dispose();
    };
  }, [scene, background]);

  return null;
}

/**
 * 棋盘格球面背景 - 使用 3D 球体几何体
 */
function CheckerboardSphere({
  background,
}: {
  background: (typeof PRESETS)[PresetKey];
}) {
  const sphereRef = useRef<THREE.Mesh>(null);
  const { scene } = useThree();

  useEffect(() => {
    // 清除原有的 scene.background
    scene.background = null;
  }, [scene]);

  useEffect(() => {
    if (!sphereRef.current) return;

    const texture = createCanvasTexture(background);

    // 设置纹理重复模式和重复次数
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;

    // 根据背景类型设置重复策略
    if (background.type === "checkerboard" && background.gradientEnabled) {
      // 渐变棋盘格：使用大Canvas，垂直不重复显示完整渐变
      texture.repeat.set(16, 1);
    } else {
      // 传统背景：保持原有的重复设置
      texture.repeat.set(16, 8);
    }

    // 创建材质
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.BackSide, // 显示球体内表面
    });

    sphereRef.current.material = material;

    return () => {
      texture.dispose();
      material.dispose();
    };
  }, [background]);

  return (
    <mesh ref={sphereRef} position={[0, 0, 0]}>
      <sphereGeometry args={[200, 64, 64]} />
    </mesh>
  );
}

/**
 * 背景渲染组件 - 支持渐变和球面棋盘格背景
 */
function BackgroundRenderer({
  preset = ENVIRONMENT_CONFIG.defaultGradient,
}: {
  preset?: PresetKey;
}) {
  const background = PRESETS[preset];

  // 棋盘格使用球面几何体，渐变使用平面背景
  if (background.type === "checkerboard") {
    return <CheckerboardSphere background={background} />;
  } else {
    return <GradientBackgroundEffect background={background} />;
  }
}

/**
 * 环境系统组件 - 内聚环境贴图和背景管理
 * 负责场景的环境光照和背景设置
 */
export function EnvironmentSystem({
  gradientPreset = ENVIRONMENT_CONFIG.defaultGradient,
}: {
  gradientPreset?: PresetKey;
} = {}) {
  return (
    <>
      {/* 背景渲染 */}
      <BackgroundRenderer preset={gradientPreset} />

      {/* HDR 环境贴图 */}
      <Environment files={ENVIRONMENT_CONFIG.hdrFile} />
    </>
  );
}
